"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import programmingLanguages from "@/data/programming_languages.json";
import StackBadges from "@/components/common/StackBadges";
import shuffleArray from "@/utils/random";
import { HORIZONTAL_LOOP_ITEM_CLASS, horizontalLoop } from "@/utils/horizontalLoop";

type StackItem = {
    name: string;
    icon: string;
};

function chunkContiguous<T>(arr: T[], parts: number): T[][] {
    const chunks: T[][] = [];
    const base = Math.floor(arr.length / parts);
    let extra = arr.length % parts;
    let start = 0;
    for (let i = 0; i < parts; i++) {
        const size = base + (extra > 0 ? 1 : 0);
        if (extra > 0) extra--;
        chunks.push(arr.slice(start, start + size));
        start += size;
    }
    return chunks;
}

const SPEEDS = [0.3, 0.6, 0.5];
const rowCount = 3;

const loopItemSelector = `.${HORIZONTAL_LOOP_ITEM_CLASS}`;

export default function StackCard({ className }: { className?: string }) {
    const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [stackItems, setStackItems] = useState<StackItem[]>(
        () => [...(programmingLanguages as StackItem[])],
    );

    useLayoutEffect(() => {
        setStackItems((prev) => {
            const next = [...prev];
            shuffleArray(next);
            return next;
        });
    }, []);

    useEffect(() => {
        const timelines: gsap.core.Timeline[] = [];
        const cleanups: (() => void)[] = [];

        rowRefs.current.forEach((row, i) => {
            if (!row) return;

            const items = Array.from(row.querySelectorAll<HTMLElement>(loopItemSelector));
            if (!items.length) return;

            const tl = horizontalLoop(items, {
                speed: SPEEDS[i],
                reversed: false,
                repeat: -1,
            });

            timelines.push(tl);

            const onEnter = () => gsap.to(tl, { timeScale: 0, overwrite: true });
            const onLeave = () => gsap.to(tl, { timeScale: 1, overwrite: true });
            items.forEach((item) => {
                item.addEventListener("mouseenter", onEnter);
                item.addEventListener("mouseleave", onLeave);
            });
            cleanups.push(() => {
                items.forEach((item) => {
                    item.removeEventListener("mouseenter", onEnter);
                    item.removeEventListener("mouseleave", onLeave);
                });
            });
        });

        return () => {
            cleanups.forEach((fn) => fn());
            timelines.forEach((tl) => tl.kill());
        };
    }, [stackItems]);

    const rows = chunkContiguous(stackItems, rowCount);

    return (
        <div className={`card py-6 ${className}`}>
            <div className="space-y-2 px-6 text-center">
                <h2 className="text-2xl font-headline font-bold">Ma Stack Technique</h2>
                <p className="text-white/70">
                    J&apos;utilise cette stack technique quotidiennement dans mes projets.
                </p>
            </div>
            <div className="space-y-6 overflow-hidden">
                {rows.map((chunk, index) => (
                    <div
                        key={index}
                        ref={(el) => {
                            rowRefs.current[index] = el;
                        }}
                        className="flex gap-3"
                    >
                        {Array.from({ length: 2 }).flatMap((_, dupIndex) =>
                            chunk.map((item) => (
                                <div
                                    key={`${item.name}-${dupIndex}`}
                                    className={`${HORIZONTAL_LOOP_ITEM_CLASS} shrink-0`}
                                >
                                    <StackBadges names={[item.name]} />
                                </div>
                            )),
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
